import * as React from "react";
import { observer } from "mobx-react";
import { Button } from "@fluentui/react-components";
import { NewShiftsDialog } from "../common/NewShiftsDialog/NewShiftsDialog";
import StringsStore from "sh-strings/store";
import AutomationUtil from "sh-application/utility/AutomationUtil";
import muteScheduleGroupDialogStore from "./store/store";
import hideMuteScheduleGroupDialog from "./mutatorActions/hideMuteScheduleGroupDialog";
import { InstrumentationService } from "sh-services";
import { getGenericEventPropertiesObject } from "sh-instrumentation";

const styles = require("./MuteScheduleGroupDialog.scss");

/**
 * Confirmation dialog shown BEFORE user mutes/unmutes a schedule group.
 * Explains the notification consequences and asks for confirmation.
 */
@observer
export default class MuteScheduleGroupDialog extends React.Component<{}, {}> {
    private _strings: Map<string, string> = StringsStore().registeredStringModules.get("schedulePage").strings;
    private _commonStrings: Map<string, string> = StringsStore().registeredStringModules.get("common").strings;

    render() {
        const store = muteScheduleGroupDialogStore();
        const { isShown, actionTaken, onConfirm, onCancel } = store;

        if (!isShown || !actionTaken) {
            return null;
        }

        const isMuting = actionTaken === "mute";
        const title = isMuting
            ? this._strings.get("muteScheduleGroupDialogMuteTitle")
            : this._strings.get("muteScheduleGroupDialogUnmuteTitle");

        const message = isMuting
            ? this._strings.get("muteScheduleGroupDialogMuteMessage")
            : this._strings.get("muteScheduleGroupDialogUnmuteMessage");

        const content = (
            <div className={styles.contentContainer}>
                <div className={styles.messageText}>{message}</div>
            </div>
        );

        const actions = (
            <>
                <Button
                    data-automation-id={AutomationUtil.getAutomationId("muteScheduleGroupDialog", "QAIDCancelButton")}
                    appearance="secondary"
                    onClick={this.onCancel}
                >
                    {this._commonStrings.get("cancel")}
                </Button>
                <Button
                    data-automation-id={AutomationUtil.getAutomationId("muteScheduleGroupDialog", "QAIDConfirmButton")}
                    appearance="primary"
                    onClick={this.onConfirm}
                >
                    {this._strings.get("muteScheduleGroupDialogConfirmButton")}
                </Button>
            </>
        );

        return (
            <NewShiftsDialog
                title={title}
                content={content}
                actions={actions}
                isOpen={isShown}
                onDismiss={this.onCancel}
                modalType="modal"
                ariaLabel={title}
            />
        );
    }

    private onConfirm = () => {
        const { actionTaken, onConfirm } = muteScheduleGroupDialogStore();

        // Log confirmation
        InstrumentationService.logEvent(InstrumentationService.events.MuteScheduleGroupDialogConfirmed, [
            getGenericEventPropertiesObject(InstrumentationService.properties.ActionTaken, actionTaken || "unknown")
        ]);

        // Execute the confirm callback (proceed with mute/unmute)
        if (onConfirm) {
            onConfirm();
        }

        // Hide the dialog
        hideMuteScheduleGroupDialog();
    }

    private onCancel = () => {
        const { actionTaken, onCancel } = muteScheduleGroupDialogStore();

        // Log cancellation
        InstrumentationService.logEvent(InstrumentationService.events.MuteScheduleGroupDialogCancelled, [
            getGenericEventPropertiesObject(InstrumentationService.properties.ActionTaken, actionTaken || "unknown")
        ]);

        // Execute the cancel callback (do nothing)
        if (onCancel) {
            onCancel();
        }

        // Hide the dialog
        hideMuteScheduleGroupDialog();
    }
}
